import { Suspense, useEffect, useRef, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { Icon } from "./icons";
import { UnlockHost } from "./Unlock";
import { useQueryClient } from "@tanstack/react-query";
import { LoadBar, PageIn } from "./LoadBar";
import { useIdleSignOut } from "../lib/idle";
import { SupportBanner } from "./SupportAccess";
import { SettingsNav, useSettingsArea } from "./SettingsTabs";
import { hoverWarm, sectionOf, setCurrentPath, useIdleWarm, useNavGlide } from "../lib/smooth";
import { api, logout } from "../lib/api";
import { arrange, layoutFor, useAppearance, useCompany, usePrefs, type Folder } from "../lib/prefs";
import { dateTime } from "../lib/format";
import { useMe, useOrg } from "../lib/session";
import { useEntitlements } from "../lib/plans";
import { roleLabel } from "../lib/format";
import { ErrorText, Loading } from "./ui";
import { useWords } from "../lib/workspace";
import { ErrorBoundary } from "./ErrorBoundary";
import { MFAGate } from "../routes/security";
import { Assistant } from "./Assistant";
import { SidebarSearch } from "./Search";
import { Logo } from "./Logo";
import { Toasts } from "./Toast";
import { useInboxCounts } from "../lib/inbox";

// HELP_EMAIL is where people write when they need a hand.
export const HELP_EMAIL = "feras@svenex.app";

export interface NavItem { key: string; label: string; to: string; end?: boolean }

/** useNavItems lists the sidebar pages this person may see, in the default
 *  order: day-to-day work first, then the AI, then results. Setup pages live
 *  under Settings (useSettingsItems). */
export function useNavItems(): NavItem[] {
  const { orgId, allows } = useOrg();
  const plan = useEntitlements();
  const w = useWords();
  const base = `/o/${orgId}`;
  const all: (NavItem & { show: boolean })[] = [
    { key: "today", label: "Today", to: base, end: true, show: true },
    { key: "inbox", label: "Inbox", to: `${base}/inbox`, show: true },
    { key: "contacts", label: w("contact", true), to: `${base}/contacts`, show: true },
    { key: "deals", label: w("deal", true), to: `${base}/deals`, show: plan.enabled("deals") },
    { key: "tasks", label: "Tasks", to: `${base}/tasks`, show: true },
    { key: "calendar", label: "Calendar", to: `${base}/calendar`, show: plan.enabled("bookings") },
    { key: "agents", label: "AI agents", to: `${base}/agents`, show: allows("agents") && plan.enabled("website_agent") },
    { key: "knowledge", label: "Knowledge", to: `${base}/knowledge`, show: allows("knowledge") && plan.enabled("knowledge_base") },
    { key: "automations", label: "Automations", to: `${base}/automations`, show: allows("automations") && plan.enabled("automations") },
    { key: "results", label: "Results", to: `${base}/results`, show: allows("contacts_all") && plan.enabled("roi_reports") },
    { key: "settings", label: "Settings", to: `${base}/settings`, end: true, show: true },
  ];
  return all.filter((i) => i.show);
}

export interface SettingsItem extends NavItem { group: string }

/** useSettingsItems lists the setup pages under Settings, grouped, that this
 *  person may open. Their addresses are the same as before, so old links work. */
export function useSettingsItems(): SettingsItem[] {
  const { orgId, allows } = useOrg();
  const me = useMe();
  const plan = useEntitlements();
  const w = useWords();
  const base = `/o/${orgId}`;
  const all: (SettingsItem & { show: boolean })[] = [
    { group: "Company", key: "general", label: "Company details", to: `${base}/settings`, end: true, show: true },
    { group: "Company", key: "brand", label: "Brand", to: `${base}/settings/brand`, show: allows("brand") },
    { group: "Company", key: "team", label: "Team", to: `${base}/team`, show: allows("team") },
    { group: "Company", key: "workspace", label: "Fields & words", to: `${base}/settings/workspace`, show: allows("workspace") },
    { group: "Company", key: "usage", label: "Plan & usage", to: `${base}/settings/usage`, show: allows("team") },
    { group: "Conversations", key: "channels", label: "Channels", to: `${base}/channels`, show: allows("agents") },
    { group: "Conversations", key: "email", label: "Email", to: `${base}/settings/email`, show: allows("email") && plan.enabled("email_channel") },
    { group: "Bookings", key: "services", label: `${w("service", true)} & hours`, to: `${base}/services`, show: plan.enabled("bookings") && allows("bookings") },
    { group: "Bookings", key: "booking-page", label: "Booking page", to: `${base}/booking-page`, show: allows("brand") && plan.enabled("bookings") },
    { group: "Security", key: "security", label: "Company security", to: `${base}/settings/security`, show: allows("team") },
    { group: "Security", key: "support", label: "SVENEX support access", to: `${base}/settings/support`, show: !me.data?.support },
    { group: "You", key: "account", label: "Your account", to: `${base}/settings/account`, show: !me.data?.support },
    { group: "You", key: "sidebar", label: "Sidebar", to: `${base}/settings/sidebar`, show: true },
  ];
  return all.filter((i) => i.show);
}

export function Layout() {
  const me = useMe();
  const { orgId, org, role } = useOrg();
  const plan = useEntitlements();
  const [assistantOpen, setAssistantOpen] = useState(() => { try { return sessionStorage.getItem("svx-assistant-open") === "1"; } catch { return false; } });
  const toggleAssistant = (open: boolean) => { setAssistantOpen(open); try { sessionStorage.setItem("svx-assistant-open", open ? "1" : "0"); } catch { /* ignore */ } };
  if (me.isLoading) return <Loading />;
  if (me.data?.deletion_scheduled_for) return <AccountLeaving when={me.data.deletion_scheduled_for} />;
  if (!org) {
    return (
      <div className="center">
        <p>You don't have access to this organization.</p>
        <Link to="/">Back</Link>
      </div>
    );
  }
  if (org.status === "closed") return <CompanyClosed orgId={orgId} name={org.display_name} purgeAfter={org.purge_after} />;
  if (!org.crm_active || !role) {
    return (
      <div className="center">
        <p>{!role ? "You don't have CRM access in this organization." : "This organization's CRM subscription is not active. Contact SVENEX to renew."}</p>
        <Link to="/">Back</Link>
      </div>
    );
  }
  if (org.require_mfa && !me.data?.session_mfa) {
    return <MFAGate what={`${org.display_name} requires everyone to sign in with an authenticator app.`} />;
  }
  return <Shell assistantOpen={assistantOpen} toggleAssistant={toggleAssistant} assistantInPlan={plan.enabled("setup_assistant")} />;
}

function Shell({ assistantOpen, toggleAssistant, assistantInPlan }: { assistantOpen: boolean; toggleAssistant: (open: boolean) => void; assistantInPlan: boolean }) {
  const me = useMe();
  const { org, role } = useOrg();
  const plan = useEntitlements();
  const items = useNavItems();
  const setupItems = useSettingsItems();
  const waiting = useInboxCounts().data?.needs_person ?? 0;
  const { prefs, save } = usePrefs();
  const settingsArea = useSettingsArea();
  const company = useCompany();
  useAppearance(company.data?.accent, company.data?.on_accent);
  const location = useLocation();
  setCurrentPath(location.pathname);
  const qc = useQueryClient();
  const { orgId, base, allows } = useOrg();
  const warmCtx = { orgId, base, allows };
  const scrollRef = useRef<HTMLDivElement>(null);
  const [collapsed, setCollapsed] = useState(() => { try { return localStorage.getItem("svx-sidebar") === "collapsed"; } catch { return false; } });
  const [navOpen, setNavOpen] = useState(false);
  useEffect(() => { setNavOpen(false); }, [location.pathname]);
  useEffect(() => {
    if (!navOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setNavOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [navOpen]);
  // The company may sign out a screen left unattended.
  useIdleSignOut(me.data?.idle_sign_out_minutes, () => {
    logout().finally(() => window.location.assign("/login?idle=" + (me.data?.idle_sign_out_minutes ?? 0)));
  });
  const toggleCollapsed = () => {
    const next = !collapsed;
    setCollapsed(next);
    try { localStorage.setItem("svx-sidebar", next ? "collapsed" : "open"); } catch { /* per-browser convenience only */ }
  };
  // The person's sidebar, or the default folders until they save their own.
  const layout = layoutFor(prefs.sidebar, items);
  const toggleFolder = (f: Folder) => {
    save({ ...prefs, sidebar: layout.map((e) => (e.folder && e.folder.id === f.id ? { folder: { ...e.folder, collapsed: !e.folder.collapsed } } : e)) }).catch(() => { /* cosmetic */ });
  };
  // When the sidebar is collapsed, folders show their pages as icons so nothing is hidden.
  const tip = (label: string) => (collapsed ? label : undefined);
  const link = (i: NavItem) => (
    <NavLink key={i.key} end={i.end} to={i.to} title={tip(i.label)} aria-label={collapsed ? i.label : undefined} {...hoverWarm(qc, i.to, warmCtx)}
      // Settings stays lit on its tabs (Security, Your account, hidden pages).
      className={i.key === "settings" ? ({ isActive }) => (isActive || settingsArea ? "active" : "") : undefined}>
      <span className="nav-ico"><Icon name={i.key} /></span><span className="nav-label">{i.label}</span>
      {i.key === "inbox" && waiting > 0 && <span className="nav-count" aria-label={`${waiting} waiting for a person`}>{waiting > 99 ? "99+" : waiting}</span>}
    </NavLink>
  );
  // Settings sits at the bottom with Get help, outside the reorderable list.
  const settingsItem = items.find((i) => i.key === "settings");
  const shown = arrange(items.filter((i) => i.key !== "settings" && !(prefs.hidden ?? []).includes(i.key)), layout);
  useNavGlide(scrollRef, [location.pathname, collapsed, JSON.stringify(layout), items.length]);
  // The pages people open most start loading quietly once the app is idle.
  useIdleWarm(qc, items.filter((i) => ["today", "inbox", "calendar", "tasks", "deals", "contacts"].includes(i.key)).map((i) => i.to), warmCtx);
  // Each page fades in once its data is here. The inbox is one page whichever
  // conversation is open, so switching conversations doesn't fade the list.
  const section = (sectionOf(location.pathname) ?? "").split("/")[0];
  // A new page starts at the top; switching conversations in the inbox doesn't move the page.
  const scrollKey = section === "inbox" ? section : location.pathname;
  useEffect(() => { window.scrollTo({ top: 0 }); }, [scrollKey]);
  const name = org!.display_name;
  const email = me.data?.user.email ?? "";
  const cls = ["shell", assistantOpen && "with-assistant", collapsed && "collapsed", navOpen && "nav-open"].filter(Boolean).join(" ");
  return (
    <div className={cls}>
      <header className="mobile-bar">
        <button className="icon-btn" aria-label="Open menu" aria-expanded={navOpen} onClick={() => setNavOpen(true)}><Icon name="menu" /></button>
        <span className="wordmark"><Logo /></span>
        <span className="muted small">{name}</span>
      </header>
      {navOpen && <div className="scrim" onClick={() => setNavOpen(false)} />}
      <nav className="sidebar" aria-label="Main">
        <div className="side-top">
          <Link to="/" className="wordmark" aria-label="SVENEX home"><Logo /></Link>
          <Link to="/" className="side-mark" aria-label="SVENEX home"><Logo mark /></Link>
          <button className="icon-btn collapse-btn" onClick={toggleCollapsed} aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"} title={collapsed ? "Expand sidebar" : "Collapse sidebar"}>
            <Icon name={collapsed ? "expand" : "collapse"} />
          </button>
          {navOpen && <button className="icon-btn" aria-label="Close menu" onClick={() => setNavOpen(false)}><Icon name="close" /></button>}
        </div>
        <div className="brand" title={tip(name)}>
          <span className="brand-mark">{company.data?.logo_url ? <img src={company.data.logo_url} alt="" /> : name.trim().charAt(0).toUpperCase()}</span>
          <span className="brand-text">
            <b>{name}</b>
            <small>{roleLabel[role!]}{plan.data?.plan_name && ` · ${plan.data.plan_name}`}</small>
          </span>
        </div>
        <SidebarSearch items={[...items, ...setupItems]} base={`/o/${orgId}`} collapsed={collapsed} />
        {assistantInPlan && !assistantOpen && (
          <button className="assistant-open" onClick={() => toggleAssistant(true)} title={tip("Setup assistant")} aria-label="Setup assistant">
            <span className="nav-ico"><Icon name="sparkle" /></span><span className="nav-label">Setup assistant</span>
          </button>
        )}
        <div className="side-scroll" ref={scrollRef}>
        <span className="nav-glide" aria-hidden />
        {shown.map((e) => "item" in e ? link(e.item) : (
          <div key={e.folder.id} className="nav-folder">
            <button type="button" className="nav-folder-head" aria-expanded={collapsed || !e.folder.collapsed} onClick={() => toggleFolder(e.folder)} title={tip(e.folder.name)} disabled={collapsed}>
              {collapsed ? <span className="nav-rule" aria-hidden /> : <><Icon name="chevron" /><span>{e.folder.name}</span></>}
            </button>
            {(collapsed || !e.folder.collapsed) && <div className="nav-folder-items">{e.items.map(link)}</div>}
          </div>
        ))}
        </div>
        <div className="side-foot">
          <a className="help-line" href={`mailto:${HELP_EMAIL}?subject=${encodeURIComponent("SVENEX help: " + name)}`} title={collapsed ? `Message ${HELP_EMAIL}` : HELP_EMAIL}>
            <span className="nav-ico"><Icon name="help" /></span><span className="nav-label">Get help</span>
          </a>
          {settingsItem && link(settingsItem)}
          {(me.data?.organizations.length ?? 0) > 1 && !me.data?.support && <Link to="/" title={tip("Switch company")}><span className="nav-ico"><Icon name="switch" /></span><span className="nav-label">Switch company</span></Link>}
          <button className="side-link" onClick={() => logout()} title={tip("Sign out")} aria-label="Sign out"><span className="nav-ico"><Icon name="signout" /></span><span className="nav-label">Sign out</span></button>
          <div className="side-user" title={email}><i aria-hidden>{email.charAt(0).toUpperCase()}</i><span>{email}</span></div>
        </div>
      </nav>
      <main className="content">
        <LoadBar />
        <SupportBanner />
        <UnlockHost />
        <Suspense fallback={<Loading />}>
          <PageIn key={section === "inbox" ? section : location.pathname}>
            {settingsArea ? (
              <div className="settings-shell">
                <SettingsNav />
                <div className="settings-body"><ErrorBoundary><Outlet /></ErrorBoundary></div>
              </div>
            ) : <ErrorBoundary><Outlet /></ErrorBoundary>}
          </PageIn>
        </Suspense>
      </main>
      {assistantOpen && assistantInPlan && <ErrorBoundary><Assistant onClose={() => toggleAssistant(false)} /></ErrorBoundary>}
      <Toasts />
    </div>
  );
}

/** CompanyClosed is what an owner sees after closing the company. */
export function CompanyClosed({ orgId, name, purgeAfter }: { orgId: string; name: string; purgeAfter?: string }) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  return (
    <div className="center">
      <h1>{name} is closed</h1>
      <p>Nobody can use it, and its AI agents, booking page and channels have stopped.{purgeAfter && <> It will be deleted for good on <b>{dateTime(purgeAfter)}</b>.</>}</p>
      <p>Changed your mind? You can reopen it until then, with everything as it was.</p>
      <ErrorText error={error} />
      <div className="inline">
        <button disabled={busy} onClick={async () => {
          setBusy(true); setError(null);
          try { await api(`/v1/orgs/${orgId}/closure`, { method: "DELETE" }); await qc.invalidateQueries({ queryKey: ["me"] }); } catch (e) { setError(e); } finally { setBusy(false); }
        }}>{busy ? "Reopening…" : `Reopen ${name}`}</button>
        <Link to="/">Back</Link>
      </div>
    </div>
  );
}

/** AccountLeaving is shown while an account waits to be deleted. */
export function AccountLeaving({ when }: { when: string }) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  return (
    <div className="center">
      <h1>Your account is set to be deleted</h1>
      <p>It will be deleted on <b>{dateTime(when)}</b>. Until then you can keep it, with everything as it was.</p>
      <ErrorText error={error} />
      <div className="inline">
        <button disabled={busy} onClick={async () => {
          setBusy(true); setError(null);
          try { await api("/v1/me/deletion", { method: "DELETE" }); await qc.invalidateQueries({ queryKey: ["me"] }); } catch (e) { setError(e); } finally { setBusy(false); }
        }}>{busy ? "Keeping…" : "Keep my account"}</button>
        <button className="link" onClick={() => logout()}>Sign out</button>
      </div>
    </div>
  );
}

/** Allowed shows a page only to roles that may use it (the server enforces the same). */
export function Allowed({ to, children }: { to: string; children: React.ReactNode }) {
  const { allows, orgId } = useOrg();
  if (allows(to)) return <>{children}</>;
  return (
    <div className="center">
      <p>Your role doesn't include this page. Ask an admin in your company if you need it.</p>
      <Link to={`/o/${orgId}`}>Back to Today</Link>
    </div>
  );
}
